// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseAvatar,
  BaseDatePicker,
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
import { JobFormModal } from '~/components/module/job';
// Others
import { IJobs, IQueryParamJob } from '~/utils/interface/jobs';
import { DateFormatEnum, RoleEnum } from '~/utils/enum/common';
import {
  IBaseMoreAction,
  IBaseOption,
  IFilterValueChange,
  IHeaderCellType,
  IPayloadCallApiFn,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DEFAULT_SORT_JOB,
  EMPTY_STRING,
  EMPTY_VALUE,
  FILTER_DEFAULT_VALUES,
  optionStatusJobKanban,
  optionsTypePage,
} from '~/utils/constants/common';
import { jobActions } from '~/thunks/job/jobSlice';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { deleteJob, getListJob } from '~/thunks/job/jobThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { formatDate, getFullName } from '~/utils/helper/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { JOB_PERMISSIONS } from '~/utils/constants/permissions';
import useDebounce from '~/utils/hooks/useDebounce';
import { getListClient } from '~/thunks/client/clientThunk';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { IClient, IOptionClient, IQueryParamClient } from '~/utils/interface/client';
// Styles, images, icons
import styles from './JobList.module.scss';
import { icons } from '~/assets';

type Props = {
  isTypePage?: string;
  onChangeType?: (value: string) => void;
};

const cx = classNames.bind(styles);

const columnsJobs = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IJobs>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'jobId',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_job_id')}</Box>
          <SortComponent name='id' sortType={getSortType('id')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IJobs) => record?.id,
      minWidth: 100,
    },
    {
      id: 'title',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_title')}</Box>
          <SortComponent name='title' sortType={getSortType('title')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IJobs) => record?.title || EMPTY_VALUE,
      minWidth: 150,
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_client')}</Box>
          <SortComponent name='client' sortType={getSortType('client')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IJobs) => {
        return (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record.client} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.client?.firstName,
                  lastName: record?.client?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.client?.clientContacts?.[0]?.email}</Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      id: 'timeline',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_timeline')}</Box>
          <SortComponent name='timeline' sortType={getSortType('timeline')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IJobs) => {
        return formatDate(record?.startDate, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE;
      },
      minWidth: 110,
    },
    {
      id: 'address',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_address')}</Box>
          <SortComponent name='delivery address' sortType={getSortType('delivery address')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IJobs) => {
        return <Typography className={cx('textAddress')}>{record?.address?.address || EMPTY_VALUE}</Typography>;
      },
      minWidth: 200,
    },
    {
      id: 'assignee',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_assignee')}</Box>
          <SortComponent name='assignee' sortType={getSortType('assignee')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IJobs) => {
        return (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record.assignedUser} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.assignedUser?.firstName,
                  lastName: record?.assignedUser?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.assignedUser?.email}</Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IJobs) => {
        return <BaseStatus label={record?.status} />;
      },
      minWidth: 120,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IJobs) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id),
                },
              ]
            : []),
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onDelete(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const JobList = (props: Props) => {
  //#region Destructuring Props
  const { isTypePage, onChangeType } = props;

  const { canCreateJobs, canViewJobs, canViewAllJobs, canDetailJobs, canEditJobs, canDeleteJobs } =
    usePermissions(JOB_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshJobList } = useAppSelector((state) => state.job);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_JOB);
  const [filterParams, setFilterParams] = useState<IQueryParamJob>(FILTER_DEFAULT_VALUES);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [paramObject, setParamObject] = useState<IQueryParamJob>({
    page: pageSelected,
    limit: limitPage,
  });
  const [listJob, setListJob] = useState<IJobs[]>();
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [idJob, setIdJob] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isOpenJobFormModal, setIsOpenJobFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListJob(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshJobList) return;

    handleGetListJob(paramObject);

    dispatch(jobActions.setRefreshJobList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshJobList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListJob = (params: IQueryParamJob) => {
    setIsLoading(true);

    dispatch(getListJob(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setListJob(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddJob = () => {
    handleShowJobFormModal();
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdJob(id);
    setIsOpenJobFormModal(true);
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailJob(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailJob(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailJob(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailJob(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailJob(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailJob(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailJob(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailJob(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailJob(id));
        break;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdJob(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdJob(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteJob(idJob))
      .unwrap()
      .then(() => {
        setIdJob(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(jobActions.setRefreshJobList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const fetchOptions = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsTypePage,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionStatusJobKanban,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsClient = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IOptionClient>> => {
      const { searchKey, page } = payload;
      const payloadParams: IQueryParamClient = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        // type: TypeCrmEnum.CLIENT,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListClient(payloadParams)).unwrap();

        const { responses: listClient, pagination } = resp?.data;

        const formatOptions: IOptionClient[] = listClient?.map((item: IClient) => ({
          value: item?.id,
          label:
            item?.firstName || item?.lastName
              ? getFullName({ firstName: item?.firstName, lastName: item?.lastName })
              : item?.entityName,
          email: item?.clientContacts?.[0]?.email || EMPTY_STRING,
        }));

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleApplyFilter = (values: IQueryParamJob | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        status: values?.status,
        startDate: values?.startDate,
        endDate: values?.endDate,
        clientId: values?.clientId,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      status: undefined,
      startDate: undefined,
      endDate: undefined,
      clientId: undefined,
    });
  };

  const handleFilterStatusChange = (
    status: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamJob>) => void
  ) => {
    if (!status) return;

    const { value } = status;

    onChange?.({ name: 'status', value: value });
    setFilterParams({ ...filterParams, status: value });
  };

  const handleFilterClientChange = (
    optionClient: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamJob>) => void
  ) => {
    if (!optionClient) return;

    const { value } = optionClient;

    onChange?.({ name: 'clientId', value: value });
    setFilterParams({ ...filterParams, clientId: value });
  };

  const handleShowJobFormModal = () => {
    setIsOpenJobFormModal((prev) => !prev);
    setIdJob(EMPTY_STRING);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleChangeDate = (date: string | { startDate: string; endDate: string }) => {
    if (typeof date !== 'string') {
      const { startDate, endDate } = date;
      setFilterParams((prev) => ({
        ...prev,
        startDate,
        endDate,
      }));
    }
  };
  //#endregion Handle Function

  return (
    <Box id='jobListComponent' className={cx('container')}>
      <BaseToolbar
        title={t('job_title_jobs_board')}
        total={t('job_title_total_jobs', { total: canViewAllJobs || canViewJobs ? totalItems : 0 })}
      >
        {canCreateJobs && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='outlined'
              className={cx('button')}
              onClick={handleAddJob}
              startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
            >
              {t('job_title_add_job')}
            </Button>
          </Box>
        )}

        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        <BaseFilters<IQueryParamJob>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseSelect
                gap={8}
                styleType='contained'
                label={t('job_title_filter_status')}
                placeholder={t('job_title_filter_all_status')}
                callApiFn={fetchOptionFilter}
                value={value?.status}
                onChange={(option) => handleFilterStatusChange(option as any, onChange)}
              />

              <BaseDatePicker
                mode='rangeDate'
                label={t('job_title_filter_date')}
                value={{
                  startDate: filterParams?.startDate || EMPTY_STRING,
                  endDate: filterParams?.endDate || EMPTY_STRING,
                }}
                onChange={handleChangeDate}
              />

              <BaseSelect
                isSearch
                gap={8}
                label={t('job_title_filter_client')}
                callApiFn={fetchOptionsClient}
                type='loadMore'
                value={value?.clientId}
                onChange={(option) => handleFilterClientChange(option as any, onChange)}
              />
            </Stack>
          )}
        </BaseFilters>

        <BaseSelect
          gap={4}
          height={39}
          width={130}
          styleType='outlined'
          placeholder={t('job_title_select')}
          callApiFn={fetchOptions}
          value={isTypePage}
          onChange={(item) => onChangeType?.(item?.value || EMPTY_STRING)}
        />
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewAllJobs || canViewJobs ? listJob : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsJobs(
            t,
            sortBy,
            handleSort,
            handleView,
            handleEdit,
            handleDelete,
            canDetailJobs,
            canEditJobs,
            canDeleteJobs
          )}
          onPageChange={handlePageChange}
          {...(canDetailJobs ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isOpenJobFormModal && <JobFormModal onClose={handleShowJobFormModal} idJob={idJob} />}
    </Box>
  );
};

export default JobList;
